import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {
  GenericSiemHttpConfig,
  MSSentinelSiemHttpConfig,
  SiemHttpConfig,
  SiemHttpStatus,
  SiemType,
} from 'interfaces'
import {SIEMService} from 'services/api'

export interface SiemHttpIntegrationQueryResult {
  status: SiemHttpStatus
  lastSynced: string
  targetTool: SiemType
  formField1: string // i.e. Ingest URL or HEC URI or Integration URL or Directory Tenant ID
  formField2: string // i.e. Access Token or HEC Token or Integration Key or Application Client ID
  formField3?: string // i.e. Client Secret
  formField4?: string // i.e. DCR Ingest URL
  formField5?: string // i.e. DCR Immutable ID
  formField6?: string // i.e. Custom Table Name
  connectionError: string
  isLoading: boolean
  isFetching: boolean
  isSuccess: boolean
  refetch: () => void
}

const siemHttpInitialState = {
  targetTool: '',
  formField1: '',
  formField2: '',
  status: '' as SiemHttpStatus,
  lastSynced: '',
  error: '',
}

export const SIEM_HTTP_QUERY_KEY = 'getSiemHttpIntegration'

export const useSiemHttpIntegrationQuery =
  (): SiemHttpIntegrationQueryResult => {
    const {data, isLoading, isFetching, isSuccess, refetch} = useQuery({
      queryKey: [SIEM_HTTP_QUERY_KEY],
      queryFn: () => SIEMService.getSiemHttpIntegration(),
      select: ({data}) => data,
      refetchOnWindowFocus: false,
    })

    const siemHttpRes = {...siemHttpInitialState, ...data}
    const siemHttpSelectors = useMemo(() => {
      const {status, lastSynced = '', error} = siemHttpRes

      return {
        status,
        lastSynced,
        connectionError: error ?? '',
        ...getSiemHttpConfigFields(
          siemHttpRes.config || ({} as SiemHttpConfig),
        ),
      }
    }, [siemHttpRes])

    return {
      ...siemHttpSelectors,
      isLoading,
      isFetching,
      isSuccess,
      refetch,
    }
  }

const getSiemHttpConfigFields = (config: SiemHttpConfig) => {
  const {targetTool, ingestURL, accessToken} = config as GenericSiemHttpConfig
  const {
    msTenantId,
    msClientId,
    msClientSecret,
    dceIngestURL,
    dcrImmutableId,
    customTableName,
  } = config as MSSentinelSiemHttpConfig

  return {
    targetTool: targetTool ?? '',
    formField1: ingestURL ?? msTenantId ?? '',
    formField2: accessToken ?? msClientId ?? '',
    formField3: msClientSecret,
    formField4: dceIngestURL,
    formField5: dcrImmutableId,
    formField6: customTableName,
  }
}
